'use client'

import { useState, useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { PublicNavbar } from '@/components/layout/public-navbar'
import { Footer } from '@/components/footer'
import { GridBackground } from '@/components/ui/grid-background'
import { SectionHeading, BodyText } from '@/components/ui/typography'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { CONTACT_TOPICS } from './topics'
import { supabase } from '@/lib/supabase'

// =============================================================================
// SPAM PROTECTION UTILITIES
// =============================================================================

const RATE_LIMIT_KEY = 'miaozi_contact_submissions'
const MAX_SUBMISSIONS_PER_HOUR = 3
const MIN_FORM_TIME_MS = 3000 // Minimum 3 seconds to fill form (bots are faster)

interface RateLimitData {
  timestamps: number[]
}

function getRateLimitData(): RateLimitData {
  if (typeof window === 'undefined') return { timestamps: [] }
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY)
    if (!stored) return { timestamps: [] }
    return JSON.parse(stored)
  } catch {
    return { timestamps: [] }
  }
}

function isRateLimited(): boolean {
  const data = getRateLimitData()
  const oneHourAgo = Date.now() - 60 * 60 * 1000
  const recentSubmissions = data.timestamps.filter((ts) => ts > oneHourAgo)
  return recentSubmissions.length >= MAX_SUBMISSIONS_PER_HOUR
}

function recordSubmission(): void {
  if (typeof window === 'undefined') return
  const data = getRateLimitData()
  const oneHourAgo = Date.now() - 60 * 60 * 1000
  // Clean old timestamps and add new one
  data.timestamps = [
    ...data.timestamps.filter((ts) => ts > oneHourAgo),
    Date.now()
  ]
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data))
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ContactPageClient() {
  const t = useTranslations('staticPages.contact')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Spam protection state
  const [honeypot, setHoneypot] = useState('') // Bots will fill this
  const formLoadTime = useRef<number>(Date.now())

  // Reset form load time when component mounts
  useEffect(() => {
    formLoadTime.current = Date.now()
  }, [])

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    topic: '',
    message: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    // --- SPAM PROTECTION CHECKS ---

    // 1. Honeypot check: if filled, silently "succeed" (don't let bot know it failed)
    if (honeypot) {
      // Fake success to confuse bots
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setIsSuccess(true)
      return
    }

    // 2. Timing check: form filled too fast = likely bot
    const timeSpent = Date.now() - formLoadTime.current
    if (timeSpent < MIN_FORM_TIME_MS) {
      // Fake success to confuse bots
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setIsSuccess(true)
      return
    }

    // 3. Rate limiting check
    if (isRateLimited()) {
      setError(t('form.errors.tooMany'))
      setIsSubmitting(false)
      return
    }

    // --- END SPAM PROTECTION ---

    try {
      const { error: submitError } = await supabase
        .from('contact_messages')
        .insert({
          name: formData.name || null,
          email: formData.email,
          topic_slug: formData.topic,
          message: formData.message
        })

      if (submitError) throw submitError

      recordSubmission() // Track for rate limiting
      setIsSuccess(true)
      setFormData({ name: '', email: '', topic: '', message: '' })
    } catch {
      setError(t('form.errors.generic'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-background flex min-h-screen flex-col">
      <PublicNavbar />

      <main className="flex-1">
        <GridBackground>
          <section className="px-4 pt-20 pb-16 sm:pt-32 sm:pb-24">
            <div className="mx-auto w-full max-w-2xl space-y-8">
              <header className="space-y-3">
                <SectionHeading>{t('title')}</SectionHeading>
                <BodyText className="text-muted-foreground">
                  {t('description')}
                </BodyText>
                <BodyText className="text-muted-foreground text-sm">
                  {t.rich('emailLine', {
                    email: (chunks) => (
                      <a
                        href="mailto:contact@miaozi.co"
                        className="text-primary hover:underline"
                      >
                        {chunks}
                      </a>
                    )
                  })}
                </BodyText>
              </header>

              {isSuccess ? (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
                  <p className="text-emerald-600 dark:text-emerald-400">
                    {t('form.submit.success')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <h2 className="text-lg font-semibold">{t('form.title')}</h2>
                    <p className="text-muted-foreground text-sm">
                      {t('form.subtitle')}
                    </p>
                  </div>

                  {/* Honeypot field - hidden from users, bots will fill it */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      opacity: 0,
                      height: 0,
                      overflow: 'hidden'
                    }}
                  >
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        {t('form.fields.name.label')}
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        placeholder={t('form.fields.name.placeholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">
                        {t('form.fields.email.label')} *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        placeholder={t('form.fields.email.placeholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="topic">
                      {t('form.fields.topic.label')} *
                    </Label>
                    <Select
                      value={formData.topic}
                      onValueChange={(value) =>
                        setFormData({ ...formData, topic: value })
                      }
                      required
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('form.fields.topic.placeholder')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_TOPICS.map((topic) => (
                          <SelectItem key={topic.slug} value={topic.slug}>
                            {t(topic.key)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">
                      {t('form.fields.message.label')} *
                    </Label>
                    <Textarea
                      id="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      placeholder={t('form.fields.message.placeholder')}
                    />
                  </div>

                  <p className="text-muted-foreground text-xs">
                    {t('form.disclaimer')}
                  </p>

                  {error && <p className="text-destructive text-sm">{error}</p>}

                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !formData.email ||
                      !formData.topic ||
                      !formData.message
                    }
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting
                      ? t('form.submit.loading')
                      : t('form.submit.label')}
                  </Button>
                </form>
              )}
            </div>
          </section>
        </GridBackground>
      </main>

      <Footer />
    </div>
  )
}
